import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Users, Check, X, Trash2, Plus, Eye, EyeOff,
  Gift, Camera, Wine, Sparkles, Music, Heart, Edit2, Image as ImageIcon,
  Bus as BusIcon, Clock, Mail, Utensils, MapPin, Sun, Coffee, FastForward
} from "lucide-react";
import DemoPanel from "@/components/wedding/DemoPanel";
import DemoBanner from "@/components/wedding/DemoBanner";

const ICON_OPTIONS = [
  { id: "gift", Icon: Gift }, { id: "camera", Icon: Camera }, { id: "wine", Icon: Wine },
  { id: "sparkles", Icon: Sparkles }, { id: "music", Icon: Music }, { id: "heart", Icon: Heart },
  { id: "users", Icon: Users }, { id: "utensils", Icon: Utensils }, { id: "bus", Icon: BusIcon },
  { id: "map-pin", Icon: MapPin }, { id: "clock", Icon: Clock }, { id: "sun", Icon: Sun },
  { id: "coffee", Icon: Coffee },
];

const Tab = ({ id, active, setActive, children, testid }) => (
  <button
    data-testid={testid}
    onClick={() => setActive(id)}
    className={`px-4 py-3 font-semibold tracking-[0.18em] text-[0.7rem] uppercase border-b-2 transition-all ${
      active === id ? "border-[color:var(--ink-wine)] text-[color:var(--ink-wine)]" : "border-transparent text-[color:var(--ink-soft)] hover:text-[color:var(--ink-wine)]"
    }`}
  >{children}</button>
);

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("rsvps");

  if (checked && !user) return <Navigate to="/admin/login" replace />;
  if (!checked) return <div className="min-h-screen flex items-center justify-center text-[color:var(--ink-soft)]">Cargando…</div>;

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <DemoBanner />
      <header className="border-b border-[color:var(--line)] bg-[color:var(--bg-paper)]/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div>
            <p className="eyebrow">Selena & Mario</p>
            <h1 className="font-script text-3xl text-[color:var(--ink-wine)] leading-none">Panel novios</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-outline" data-testid="admin-go-home">Ver web</Link>
            <button onClick={handleLogout} className="btn-outline" data-testid="admin-logout">
              <LogOut size={12} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-3 flex gap-1 overflow-x-auto">
          <Tab id="rsvps" active={tab} setActive={setTab} testid="tab-rsvps">Confirmaciones</Tab>
          <Tab id="messages" active={tab} setActive={setTab} testid="tab-messages">Mensajes</Tab>
          <Tab id="program" active={tab} setActive={setTab} testid="tab-program">Programa</Tab>
          <Tab id="surprises" active={tab} setActive={setTab} testid="tab-surprises">Sorpresas</Tab>
          <Tab id="gallery" active={tab} setActive={setTab} testid="tab-gallery">Galería</Tab>
          <Tab id="demo" active={tab} setActive={setTab} testid="tab-demo">Demo Vivo</Tab>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {tab === "rsvps" && <RSVPsPanel />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "program" && <TimelinePanel source="program" label="Programa" />}
        {tab === "surprises" && <SurprisesPanel />}
        {tab === "gallery" && <GalleryPanel />}
        {tab === "demo" && <DemoPanel />}
      </main>
    </div>
  );
}

// ----- RSVPs -----
function RSVPsPanel() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/rsvp"), api.get("/rsvp/stats")]);
      setItems(a.data); setStats(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta confirmación?")) return;
    try { await api.delete(`/rsvp/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="rsvps-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Respuestas" value={stats?.total_responses ?? 0} Icon={Users} testid="stat-responses" />
        <Stat label="Asisten" value={stats?.attending ?? 0} Icon={Check} testid="stat-attending" />
        <Stat label="Total invitados" value={stats?.total_guests ?? 0} Icon={Users} testid="stat-total-guests" />
        <Stat label="Bus ida / vuelta" value={`${stats?.bus_outbound ?? 0}/${stats?.bus_return ?? 0}`} Icon={BusIcon} testid="stat-bus" />
      </div>

      {loading ? <p>Cargando…</p> : items.length === 0 ? (
        <div className="s-card s-card-pad text-center" data-testid="rsvps-empty">
          <p className="text-[color:var(--ink-soft)]">Aún no hay confirmaciones.</p>
        </div>
      ) : (
        <div className="s-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[color:var(--bg-blush)]">
              <tr>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">Nombre</th>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">¿Viene?</th>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">Acompañante</th>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">Niños</th>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">Bus</th>
                <th className="px-3 py-2 text-[0.65rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold">Alergias / Notas</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-[color:var(--line)]" data-testid={`rsvp-row-${r.id}`}>
                  <td className="px-3 py-2 text-[color:var(--ink-deep)]">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-[color:var(--ink-soft)]">{new Date(r.created_at).toLocaleString("es-ES")}</div>
                  </td>
                  <td className="px-3 py-2">
                    {r.attending ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Sí</span> : <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">No</span>}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--ink-deep)]">{r.companion_name || "—"}</td>
                  <td className="px-3 py-2 text-[color:var(--ink-deep)]">{r.children}</td>
                  <td className="px-3 py-2 text-[color:var(--ink-deep)] text-xs">
                    {r.bus_outbound === null && r.bus_return === null ? "—" : (
                      <>
                        I:{r.bus_outbound ? "Sí" : "No"} · V:{r.bus_return ? "Sí" : "No"}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--ink-deep)] text-xs">
                    {r.dietary && <div><span className="text-[color:var(--ink-wine)] font-semibold">Dieta:</span> {r.dietary}</div>}
                    {r.notes && <div className="italic text-[color:var(--ink-soft)]">"{r.notes}"</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(r.id)} className="text-red-600 p-1" data-testid={`rsvp-delete-${r.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, Icon, testid }) => (
  <div className="s-card s-card-pad flex items-center gap-3" data-testid={testid}>
    <div className="w-9 h-9 rounded-full bg-[color:var(--olive)] text-white flex items-center justify-center shrink-0"><Icon size={16} /></div>
    <div>
      <div className="font-serif text-xl text-[color:var(--ink-wine)] leading-none">{value}</div>
      <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--ink-wine)] font-semibold mt-1">{label}</div>
    </div>
  </div>
);

// ----- Messages -----
function MessagesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/messages"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar mensaje?")) return;
    try { await api.delete(`/messages/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="messages-panel">
      {loading ? <p>Cargando…</p> : items.length === 0 ? (
        <div className="s-card s-card-pad text-center"><p className="text-[color:var(--ink-soft)]">Aún no hay mensajes.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((m) => (
            <article key={m.id} className="s-card s-card-pad relative" data-testid={`message-${m.id}`}>
              <div className="flex items-center gap-2 mb-2 text-[color:var(--ink-wine)]">
                <Mail size={14} />
                <span className="text-xs tracking-[0.2em] uppercase font-semibold">{m.name || "Anónimo"}</span>
              </div>
              <p className="text-[color:var(--ink-deep)] whitespace-pre-wrap">{m.message}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-[color:var(--ink-soft)]">{new Date(m.created_at).toLocaleString("es-ES")}</span>
                <button onClick={() => remove(m.id)} className="text-red-600 p-1" data-testid={`message-delete-${m.id}`}><Trash2 size={14} /></button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Timeline (programa / bus) -----
function TimelinePanel({ source, label }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${source}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [source]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar evento?")) return;
    try { await api.delete(`/${source}/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`${source}-panel`}>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[color:var(--ink-soft)]">Gestiona los eventos de <strong>{label}</strong>. El más reciente activo se marca automáticamente según la hora actual.</p>
        <button onClick={() => setCreating(true)} className="btn-outline" data-testid={`${source}-create-btn`}>
          <Plus size={12} /> Nuevo
        </button>
      </div>
      {loading ? <p>Cargando…</p> : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} className="s-card s-card-pad flex items-start gap-3" data-testid={`${source}-item-${e.id}`}>
              <div className="w-12 text-right">
                <div className="font-serif text-lg text-[color:var(--ink-wine)] leading-none">
                  {new Date(e.time_iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Madrid" })}
                </div>
                <div className="text-[0.6rem] uppercase tracking-[0.18em] text-[color:var(--ink-wine)]/70 mt-1">
                  {new Date(e.time_iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", timeZone: "Europe/Madrid" })}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[color:var(--ink-deep)]">{e.title}</h4>
                {e.description && <p className="text-sm text-[color:var(--ink-soft)]">{e.description}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(e)} className="p-2 text-[color:var(--ink-wine)]" data-testid={`${source}-edit-${e.id}`}><Edit2 size={14} /></button>
                <button onClick={() => remove(e.id)} className="p-2 text-red-600" data-testid={`${source}-delete-${e.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <TimelineModal
          source={source}
          item={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TimelineModal({ source, item, onClose, onSaved }) {
  const editing = !!item;
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [icon, setIcon] = useState(item?.icon || "clock");
  const [timeIso, setTimeIso] = useState(item?.time_iso || "2026-07-18T17:30:00+02:00");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { title, description, icon, time_iso: timeIso };
      if (editing) await api.patch(`/${source}/${item.id}`, body);
      else await api.post(`/${source}`, body);
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="s-card s-card-pad w-full max-w-lg" data-testid="tl-modal">
        <h3 className="font-script-alt text-3xl text-[color:var(--ink-wine)] mb-3">{editing ? "Editar" : "Nuevo"} evento</h3>
        <div className="space-y-3">
          <div><label className="label">Título</label><input value={title} onChange={e => setTitle(e.target.value)} className="field" required data-testid="tl-title" /></div>
          <div><label className="label">Descripción</label><textarea value={description} onChange={e => setDescription(e.target.value)} className="field" rows={2} data-testid="tl-desc" /></div>
          <div>
            <label className="label">Hora (ISO con zona, ej: 2026-07-18T17:30:00+02:00)</label>
            <input value={timeIso} onChange={e => setTimeIso(e.target.value)} className="field" required data-testid="tl-time" />
          </div>
          <div>
            <label className="label">Icono</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(({ id, Icon }) => (
                <button key={id} type="button" onClick={() => setIcon(id)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border ${icon === id ? "bg-[color:var(--olive)] text-white border-transparent" : "border-[color:var(--line)] text-[color:var(--ink-wine)]"}`}
                  data-testid={`tl-icon-${id}`}
                  aria-label={id}
                ><Icon size={15} /></button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-outline" data-testid="tl-cancel">Cancelar</button>
          <button type="submit" disabled={loading} className="btn-primary !w-auto" data-testid="tl-save">{loading ? "..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

// ----- Surprises -----
function SurprisesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/surprises/all"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleUnlock = async (s) => {
    try { await api.patch(`/surprises/${s.id}`, { unlocked: !s.unlocked }); toast.success(s.unlocked ? "Ocultada" : "Desbloqueada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.title}"?`)) return;
    try { await api.delete(`/surprises/${s.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="surprises-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[color:var(--ink-soft)]">Crea sorpresas y desbloquéalas cuando quieras.</p>
        <button onClick={() => setCreating(true)} className="btn-outline" data-testid="surprise-create-btn"><Plus size={12} /> Nueva</button>
      </div>
      {loading ? <p>Cargando…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s) => {
            const Icon = (ICON_OPTIONS.find(o => o.id === s.icon) || ICON_OPTIONS[0]).Icon;
            return (
              <div key={s.id} className="s-card s-card-pad" data-testid={`admin-surprise-${s.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[color:var(--olive)] text-white flex items-center justify-center"><Icon size={14} /></div>
                    <h4 className="font-script-alt text-2xl text-[color:var(--ink-wine)]">{s.title}</h4>
                  </div>
                  <span className={`text-[0.6rem] tracking-[0.18em] uppercase px-2 py-1 rounded-full ${s.unlocked ? "bg-green-100 text-green-800" : "bg-[color:var(--bg-blush)] text-[color:var(--ink-wine)]"}`}>
                    {s.unlocked ? "Visible" : "Bloqueada"}
                  </span>
                </div>
                {s.unlock_at && (
                  <p className="text-xs text-[color:var(--olive-deep)] mb-1 font-semibold tracking-[0.1em]">
                    ⏰ Se desbloquea automáticamente el {new Date(s.unlock_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" })}
                  </p>
                )}
                {!s.unlock_at && !s.unlocked && (
                  <p className="text-xs text-[color:var(--ink-soft)] mb-1 italic">Sin horario · solo se desbloqueará manualmente</p>
                )}
                <p className="text-sm text-[color:var(--ink-deep)] mb-3">{s.description}</p>
                <div className="flex gap-1.5 flex-wrap">
                  <button onClick={() => toggleUnlock(s)} className="btn-outline !py-1.5 !px-3 !text-[0.65rem]" data-testid={`surprise-toggle-${s.id}`}>
                    {s.unlocked ? <><EyeOff size={12} /> Ocultar</> : <><Eye size={12} /> Desbloquear</>}
                  </button>
                  <button onClick={() => setEditing(s)} className="btn-outline !py-1.5 !px-3 !text-[0.65rem]" data-testid={`surprise-edit-${s.id}`}><Edit2 size={12} /> Editar</button>
                  <button onClick={() => remove(s)} className="!py-1.5 !px-3 !text-[0.65rem] inline-flex items-center gap-1 rounded-full text-red-700 border border-red-200 hover:bg-red-50" data-testid={`surprise-delete-${s.id}`}>
                    <Trash2 size={12} /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(creating || editing) && (
        <SurpriseModal surprise={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function SurpriseModal({ surprise, onClose, onSaved }) {
  const editing = !!surprise;
  const [title, setTitle] = useState(surprise?.title || "");
  const [description, setDescription] = useState(surprise?.description || "");
  const [icon, setIcon] = useState(surprise?.icon || "gift");
  const [unlocked, setUnlocked] = useState(surprise?.unlocked ?? false);
  // unlock_at comes from backend as "2026-07-18T17:30:00+02:00"; <input type="datetime-local"> needs "2026-07-18T17:30"
  const initialUnlockAt = (() => {
    if (!surprise?.unlock_at) return "";
    const m = surprise.unlock_at.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    return m ? m[1] : "";
  })();
  const [unlockAt, setUnlockAt] = useState(initialUnlockAt);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      // Convert "2026-07-18T17:30" → "2026-07-18T17:30:00+02:00" (Madrid summer time)
      const unlockIso = unlockAt ? `${unlockAt}:00+02:00` : null;
      const payload = { title, description, icon, unlocked, unlock_at: unlockIso };
      if (editing) await api.patch(`/surprises/${surprise.id}`, payload);
      else await api.post("/surprises", payload);
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="s-card s-card-pad w-full max-w-lg" data-testid="surprise-modal">
        <h3 className="font-script-alt text-3xl text-[color:var(--ink-wine)] mb-3">{editing ? "Editar sorpresa" : "Nueva sorpresa"}</h3>
        <div className="space-y-3">
          <div><label className="label">Título</label><input value={title} onChange={e => setTitle(e.target.value)} className="field" required data-testid="surprise-title-input" /></div>
          <div><label className="label">Descripción</label><textarea value={description} onChange={e => setDescription(e.target.value)} className="field" rows={3} required data-testid="surprise-description-input" /></div>
          <div>
            <label className="label">Hora de auto-desbloqueo (opcional)</label>
            <input
              type="datetime-local"
              value={unlockAt}
              onChange={e => setUnlockAt(e.target.value)}
              className="field"
              data-testid="surprise-unlock-at-input"
            />
            <p className="text-xs text-[color:var(--ink-soft)] mt-1">
              Si pones hora, se desbloqueará sola cuando llegue ese momento. Si la dejas vacía, se desbloquea manualmente.
            </p>
            {unlockAt && (
              <button type="button" onClick={() => setUnlockAt("")} className="text-xs text-[color:var(--ink-wine)] underline mt-1" data-testid="surprise-unlock-at-clear">
                Quitar hora (volver a manual)
              </button>
            )}
          </div>
          <div>
            <label className="label">Icono</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(({ id, Icon }) => (
                <button key={id} type="button" onClick={() => setIcon(id)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border ${icon === id ? "bg-[color:var(--olive)] text-white border-transparent" : "border-[color:var(--line)] text-[color:var(--ink-wine)]"}`}
                  data-testid={`surprise-icon-${id}`} aria-label={id}
                ><Icon size={15} /></button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={unlocked} onChange={e => setUnlocked(e.target.checked)} data-testid="surprise-unlocked-checkbox" />
            <span className="text-sm">Forzar visible ahora (override del horario)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-outline" data-testid="surprise-modal-cancel">Cancelar</button>
          <button type="submit" disabled={loading} className="btn-primary !w-auto" data-testid="surprise-modal-save">{loading ? "..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

// ----- Gallery panel -----
function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/gallery/all"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const backfillThumbs = async () => {
    if (!window.confirm("Generar miniaturas de los vídeos que aún no tienen. Puede tardar un poco.")) return;
    setBackfilling(true);
    let totalOk = 0, totalFailed = 0;
    try {
      // Process in batches of 10 (backend cap) — keep going until nothing left.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.post("/gallery/backfill-thumbs?limit=10");
        totalOk += data.ok || 0;
        totalFailed += data.failed || 0;
        toast.info(`Generadas ${totalOk} miniaturas · ${data.remaining} pendientes`);
        if (!data.remaining || data.processed === 0) break;
      }
      toast.success(`Listo · ${totalOk} miniaturas generadas${totalFailed ? ` · ${totalFailed} con error` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackfilling(false); }
  };

  const videosWithoutThumb = items.filter(i => i.media_type === "video" && !i.thumb_path).length;

  return (
    <div data-testid="gallery-panel">
      <div className="flex gap-2 mb-5 flex-wrap items-center">
        {["all", "invitados", "profesionales", "sorpresa"].map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} className={`pill-yn !py-1.5 !px-3 !text-xs ${filter === cat ? "active" : ""}`} data-testid={`gallery-filter-${cat}`}>
            {cat === "all" ? "Todas" : cat}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={backfillThumbs}
          disabled={backfilling || videosWithoutThumb === 0}
          className="btn-outline !px-3 !py-1.5 !text-xs disabled:opacity-40"
          data-testid="gallery-backfill-thumbs"
          title={videosWithoutThumb === 0 ? "Todos los vídeos ya tienen miniatura" : `Generar miniaturas para ${videosWithoutThumb} vídeos sin miniatura`}
        >
          {backfilling ? "Generando…" : `Miniaturas de vídeos${videosWithoutThumb > 0 ? ` (${videosWithoutThumb})` : ""}`}
        </button>
      </div>
      {loading ? <p>Cargando…</p> : filtered.length === 0 ? (
        <div className="s-card s-card-pad text-center"><ImageIcon className="mx-auto text-[color:var(--ink-wine)] mb-2" size={32} /><p className="text-[color:var(--ink-soft)]">Sin elementos.</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {filtered.map((it) => (
            <div key={it.id} className="s-card overflow-hidden relative group" data-testid={`gallery-admin-${it.id}`}>
              {it.media_type === "video" ? (
                <video src={it.media_base64} className="w-full h-40 object-cover" />
              ) : (
                <img src={it.media_base64} alt={it.caption || it.author} className="w-full h-40 object-cover" />
              )}
              <div className="p-2 text-xs">
                <div className="font-medium text-[color:var(--ink-deep)] truncate">{it.author}</div>
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-[color:var(--ink-wine)]">{it.category}</div>
              </div>
              <button onClick={() => remove(it.id)} className="absolute top-1 right-1 p-1.5 rounded-full bg-white/90 text-red-700 opacity-0 group-hover:opacity-100 transition" data-testid={`gallery-admin-delete-${it.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
